import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'

interface MarketState {
  scrollPosition: number
  activeTab: 'for-sale' | 'sold' | 'removed'
  expandedTables: {
    forSale: boolean
    sold: boolean
    removed: boolean
  }
  tableScrollPositions: {
    forSale: number
    sold: number
    removed: number
  }
  /** Aircraft ID to scroll to when returning from detail page */
  lastViewedAircraftId?: number
  /** Unique row identifier to scroll to (for sold/removed tables with transaction/event IDs) */
  lastViewedRowId?: string
}

interface GlobalColumnPreferences {
  hideExternalColumns: boolean
  hideRelativeValueColumn: boolean
}

interface MarketStateContextType {
  getMarketState: (modelId: string) => MarketState | null
  setMarketState: (modelId: string, state: MarketState) => void
  clearMarketState: (modelId: string) => void
  getGlobalColumnPreferences: () => GlobalColumnPreferences
  setGlobalColumnPreferences: (preferences: GlobalColumnPreferences) => void
}

const defaultState: MarketState = {
  scrollPosition: 0,
  activeTab: 'for-sale',
  expandedTables: {
    forSale: true,
    sold: false,
    removed: false
  },
  tableScrollPositions: {
    forSale: 0,
    sold: 0,
    removed: 0
  }
}

const defaultGlobalColumnPreferences: GlobalColumnPreferences = {
  hideExternalColumns: true,
  hideRelativeValueColumn: true
}

const MarketStateContext = createContext<MarketStateContextType | undefined>(undefined)

export function MarketStateProvider({ children }: { children: ReactNode }) {
  const [marketStates, setMarketStates] = useState<Record<string, MarketState>>({})
  const [globalColumnPreferences, setGlobalColumnPreferencesState] = useState<GlobalColumnPreferences>(defaultGlobalColumnPreferences)

  // Load state from sessionStorage on mount
  useEffect(() => {
    const savedStates = sessionStorage.getItem('marketStates')
    if (savedStates) {
      try {
        setMarketStates(JSON.parse(savedStates))
      } catch (e) {
        // Failed to parse market states
      }
    }

    // Load global column preferences from localStorage
    const savedGlobalPreferences = localStorage.getItem('globalColumnPreferences')
    if (savedGlobalPreferences) {
      try {
        setGlobalColumnPreferencesState(JSON.parse(savedGlobalPreferences))
      } catch (e) {
        // Failed to parse global column preferences
      }
    }
  }, [])

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('marketStates', JSON.stringify(marketStates))
  }, [marketStates])

  // Save global column preferences to localStorage for persistence across sessions
  useEffect(() => {
    localStorage.setItem('globalColumnPreferences', JSON.stringify(globalColumnPreferences))
  }, [globalColumnPreferences])

  const getMarketState = useCallback((modelId: string): MarketState | null => {
    return marketStates[modelId] || null
  }, [marketStates])

  const setMarketState = useCallback((modelId: string, state: MarketState) => {
    setMarketStates(prev => {
      const newStates = {
        ...prev,
        [modelId]: state
      }
      // Save immediately to sessionStorage to ensure it persists before navigation
      sessionStorage.setItem('marketStates', JSON.stringify(newStates))
      return newStates
    })
  }, [])

  const clearMarketState = useCallback((modelId: string) => {
    setMarketStates(prev => {
      const newStates = { ...prev }
      delete newStates[modelId]
      return newStates
    })
  }, [])

  const getGlobalColumnPreferences = useCallback((): GlobalColumnPreferences => {
    return globalColumnPreferences
  }, [globalColumnPreferences])

  const setGlobalColumnPreferences = useCallback((preferences: GlobalColumnPreferences) => {
    setGlobalColumnPreferencesState(preferences)
  }, [])

  return (
    <MarketStateContext.Provider value={{
      getMarketState,
      setMarketState,
      clearMarketState,
      getGlobalColumnPreferences,
      setGlobalColumnPreferences
    }}>
      {children}
    </MarketStateContext.Provider>
  )
}

export function useMarketState() {
  const context = useContext(MarketStateContext)
  if (context === undefined) {
    throw new Error('useMarketState must be used within a MarketStateProvider')
  }
  return context
}

export { defaultState }